import { useCallback, useEffect, useReducer } from "react";

export const COOLDOWN_SECONDS = 60;
const STORAGE_KEY = "fansta:sms-send-cooldown";

type StoredCooldown = {
  phoneNumber: string;
  expiresAt: number; // epoch ms
};

type SendCodeCooldown = {
  remainingSeconds: number;
  isCoolingDown: boolean;
  startCooldown: () => void;
};

const readStored = (): StoredCooldown | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredCooldown;
    if (
      typeof parsed?.phoneNumber !== "string" ||
      typeof parsed?.expiresAt !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const remainingSecondsFor = (phoneNumber: string | undefined): number => {
  const stored = readStored();
  if (!stored || !phoneNumber || stored.phoneNumber !== phoneNumber) {
    return 0;
  }
  const remainingMs = stored.expiresAt - Date.now();
  return remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0;
};

/**
 * Throttles SMS verification-code sends with a 60s cooldown.
 *
 * The deadline is persisted in localStorage (keyed by phone number) so it
 * survives the navigation back from the code-input screen to the phone-input
 * form, where the user can re-send.
 *
 * The remaining time is derived from the stored deadline during render rather
 * than from state updated in an effect. That way, completing the phone number
 * reflects the cooldown in the same render and avoids a one-frame flash of the
 * enabled button before the effect would have caught up.
 */
export const useSendCodeCooldown = (phoneNumber?: string): SendCodeCooldown => {
  // Re-render trigger: bumped on each tick and after startCooldown writes storage.
  const [, tick] = useReducer((count: number): number => count + 1, 0);

  const remainingSeconds = remainingSecondsFor(phoneNumber);
  const isCoolingDown = remainingSeconds > 0;

  useEffect(() => {
    if (!isCoolingDown) {
      return;
    }
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isCoolingDown, phoneNumber]);

  const startCooldown = useCallback(() => {
    if (typeof window === "undefined" || !phoneNumber) {
      return;
    }
    const payload: StoredCooldown = {
      phoneNumber,
      expiresAt: Date.now() + COOLDOWN_SECONDS * 1000,
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Ignore write failures (e.g. storage disabled or quota exceeded).
    }
    tick();
  }, [phoneNumber]);

  return {
    remainingSeconds,
    isCoolingDown,
    startCooldown,
  };
};
